import { LitElement, html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';
import { watchAll } from '../utilities/watch';
import { EventEmitterMixin } from './event_emitter';
import { FormAssociatedRequiredMixin } from './form_associated_required_mixin';

import {
  emailValidator,
  maxLengthValidator,
  maxValidator,
  minLengthValidator,
  minValidator,
  patternValidator,
  requiredNumberValidator,
  requiredValidator,
  stepValidator,
  urlValidator,
  minWordValidator,
  maxWordValidator,
} from '../utilities/validators';

export class FloatField extends FormAssociatedRequiredMixin(
  EventEmitterMixin(LitElement)
) {
  createRenderRoot() {
    return this;
  }

  static get properties() {
    return {
      value: { type: String, reflect: true },
      type: { type: String, reflect: true },
      inputmode: { type: String },
      pattern: { type: String },
      minLength: { type: Number, attribute: 'minlength' },
      maxLength: { type: Number, attribute: 'maxlength' },
      minWords: { type: Number, attribute: 'min-words' },
      maxWords: { type: Number, attribute: 'max-words' },
      min: { type: Number },
      max: { type: Number },
      step: { type: Number },
      autofocus: { type: Boolean },
      autocomplete: { type: String },
      validateOnly: {
        type: Boolean,
        reflect: true,
        attribute: 'validate-only',
      },
      tabIndex: { type: Number },
      outlined: { type: Boolean, reflect: true },
      readOnly: { type: Boolean, reflect: true, attribute: 'readonly' },
      placeholder: { type: String, reflect: true },
      label: { type: String },
      id: { type: String },
      hasInteracted: { type: Boolean, state: false, reflect: false },
    };
  }

  constructor() {
    super();
    this._value = this.getAttribute('value') || '';
    this.type = 'text';
    this.validateOnly = false;
    this.tabIndex = 0;
    this.outlined = false;
    this.readOnly = false;
    this.hasInteracted = false;

    this.validators = [
      {
        ...requiredValidator,
        isValid: () =>
          this.isStringType
            ? requiredValidator.isValid(this)
            : requiredNumberValidator.isValid(this),
      },
      {
        ...minWordValidator,
        isValid: () =>
          this.isStringType ? minWordValidator.isValid(this) : true,
      },
      {
        ...maxWordValidator,
        isValid: () =>
          this.isStringType ? maxWordValidator.isValid(this) : true,
      },
      {
        ...minLengthValidator,
        isValid: () =>
          this.isStringType ? minLengthValidator.isValid(this) : true,
      },
      {
        ...maxLengthValidator,
        isValid: () =>
          this.isStringType ? maxLengthValidator.isValid(this) : true,
      },
      {
        ...minValidator,
        isValid: () => (this.isStringType ? true : minValidator.isValid(this)),
      },
      {
        ...maxValidator,
        isValid: () => (this.isStringType ? true : maxValidator.isValid(this)),
      },
      {
        ...stepValidator,
        isValid: () => (this.isStringType ? true : stepValidator.isValid(this)),
      },
      {
        ...patternValidator,
        isValid: () =>
          this.isStringType ? patternValidator.isValid(this) : true,
      },
      {
        key: 'typeMismatch',
        isValid: () => {
          switch (this.type) {
            case 'email':
              return emailValidator.isValid(this);
            case 'url':
              return urlValidator.isValid(this);
            default:
              return true;
          }
        },
        message: () =>
          this.type === 'email' ? emailValidator.message : urlValidator.message,
      },
    ];

    watchAll(this, [
      {
        propName: 'min',
        handlerName: 'handleMinChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'max',
        handlerName: 'handleMaxChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'minLength',
        handlerName: 'handleMinLengthChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'maxLength',
        handlerName: 'handleMaxLengthChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'minWords',
        handlerName: 'handleMinWordsChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'maxWords',
        handlerName: 'handleMaxWordsChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'pattern',
        handlerName: 'handlePatternChange',
        options: { waitUntilFirstUpdate: true },
      },
      {
        propName: 'step',
        handlerName: 'handleStepChange',
        options: { waitUntilFirstUpdate: true },
      },
    ]);
  }

  get isStringType() {
    return this.type !== 'number';
  }

  get value() {
    return this._value?.toString() || '';
  }

  set value(value) {
    this._value = value ?? '';

    if (!this.hasInteracted) return;
    this.setFormValue(value || null);
    this.updateValidity();
    this.setInvalidState();
  }

  focus(options) {
    this.input.focus(options);
  }

  blur() {
    this.input.blur();
  }

  select() {
    return this.input.select();
  }

  stepUp(n) {
    this.input.stepUp(n);
    this.value = this.input.value;
  }

  constraintsChanged() {
    this.updateValidity();
  }

  connectedCallback() {
    super.connectedCallback();

    this.setFormValue(this._value ? this._value.toString() : null);
    this.updateValidity();
  }

  firstUpdated() {
    if (this.isConnected && this.type !== 'checkbox' && this.type !== 'textarea') {
      setTimeout(() => {
        if (this.input && this.input?.value && this.input?.value !== this.value) {
          if (!this.input?.value) {
            return
          }

          this.hasInteracted = true;
          this.value = this.input?.value;
        }
      }, 100);
    }
  }

  handleInput(e) {
    if ('insertReplacementText' === e.inputType || !('data' in e)) {
      this.hasInteracted = true;
    }
    
    this.value = this.input.value;
    this.emitEvent('input', { detail: this.value });
  }

  handleChange() {
    this.value = this.input.value;
    this.hasInteracted = true;
    this.emitEvent('change', { detail: this.value });
  }

  handleFocus() {
    this._dirty = true;
    this.emitEvent('focus');
  }

  handleBlur() {
    this.hasInteracted = true;
    this.value = this.input.value;
    this.checkValidity();
    this.setInvalidState();
    this.emitEvent('blur');
  }

  setSelectionRange(start, end, direction = 'none') {
    this.input.setSelectionRange(start, end, direction);
  }

  setRangeText(replacement, start, end, selectMode = 'preserve') {
    this.input.setRangeText(replacement, start, end, selectMode);
    this.value = this.input.value;
  }

  renderLabel() {
    return this.label && this.type !== 'tel'
      ? html`<label for="${this.id}"> ${this.label} </label>`
      : nothing;
  }

  renderInput() {
    return html`
      <input
        ${ref((el) => (this.input = el))}
        id=${this.id}
        .type=${this.type}
        name=${ifDefined(this.name)}
        pattern=${ifDefined(this.pattern)}
        placeholder=${ifDefined(this.placeholder)}
        .value=${live(this.value)}
        ?readonly=${this.readOnly}
        ?disabled=${this.disabled}
        ?required=${this.required}
        ?autofocus=${this.autofocus}
        tabindex=${this.tabIndex}
        autocomplete=${ifDefined(this.autocomplete)}
        inputmode=${ifDefined(this.inputmode)}
        min=${ifDefined(this.validateOnly ? undefined : this.min)}
        max=${ifDefined(this.validateOnly ? undefined : this.max)}
        minlength=${ifDefined(this.minLength)}
        maxlength=${ifDefined(this.validateOnly ? undefined : this.maxLength)}
        step=${ifDefined(this.step)}
        aria-invalid=${this.invalid ? 'true' : 'false'}
        @change=${this.handleChange}
        @input=${this.handleInput}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
      />
    `;
  }

  renderValid() {
    return this.validationMessage
      ? html`<span class="float_field_error">
          <svg
            width="13"
            height="13"
            viewBox="0 0 29 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M27.723 19.2958L16.594 1.58862C15.9495 0.563405 15.0804 0 14.1436 0C13.2061 0 12.3366 0.563859 11.6923 1.58953L0.589826 19.2689C-0.0544873 20.2939 -0.175124 21.2728 0.248597 22.0282C0.675535 22.7884 1.59053 23.2059 2.82585 23.2073L25.4945 23.2244H25.4752V23.2266C26.5823 23.2266 27.6328 22.811 28.0593 22.0532C28.4831 21.3001 28.3673 20.3214 27.723 19.2958ZM14.7613 19.6921C14.5966 19.8546 14.3744 19.9469 14.1414 19.9469C13.9088 19.9469 13.6825 19.8537 13.5171 19.6912C13.353 19.5287 13.2599 19.3019 13.2602 19.0701C13.2606 18.8396 13.3151 18.6142 13.4796 18.453C13.6476 18.2889 13.79 18.1951 14.0115 18.1951L14.0529 18.1946C14.285 18.1946 14.5607 18.2889 14.7276 18.4539C14.8925 18.6162 15.0109 18.841 15.0104 19.0719C15.0102 19.3042 14.9275 19.5303 14.7613 19.6921ZM14.1441 16.4426C13.9075 16.4426 13.688 16.3517 13.5207 16.1862C13.3534 16.0206 13.2629 15.8003 13.2631 15.5658L13.1703 7.66426C13.1712 7.18062 13.5134 6.78835 13.9564 6.78835H14.0582C14.2951 6.78835 14.5683 6.87949 14.7354 7.04517C14.9029 7.21108 15.0187 7.43085 15.0185 7.66539L15.0242 15.5669C15.0237 16.0496 14.6329 16.4426 14.1441 16.4426Z"
              fill="currentColor"
            />
          </svg>
          ${this.validationMessage}
        </span> `
      : nothing;
  }

  render() {
    return html`<div class="${this.tagName.toLowerCase()}">
      ${this.renderInput()} ${this.renderLabel()}
    </div> ${this.invalid ? this.renderValid() : nothing}`;
  }
}

window.customElements.get('float-field') ||
  window.customElements.define('float-field', FloatField);
